import { Button, Card, Empty, Grid, InputNumber, Message, Select, Space, Table } from "@arco-design/web-react"
import { useEffect, useState } from "react";
import { CustomerAPI, SOFulfillmentAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { Currency, Number } from "@/components/Numeric";
import _ from "lodash";
import { FileUtil, UserProfile, UserProfileType } from "@/utils";
import { Link } from "react-router-dom";
import Reference from "@/components/Reference";
import dayjs from "dayjs";

const Row = Grid.Row;
const Col = Grid.Col;

const ColumnsOptions = [
    "Fulfillment#",
    "Fulfillment Date",
    "SO#",
    "Customer",
    "Reference",
    "SO Date",
    "Item Number",
    "Item Name",
    "Item Description",
    "Fulfilled Qty",
    "Fulfilled Amount",
    "SO Price",
];

const AllColumns = [
    {
        title: "Fulfillment#",
        render: (col, record) => <Link to={`/workspace/sales/so-fulfillment/${record.soFulfillmentId}`}>{record.fulfillment.soFulfillmentNo}</Link>,
    }, {
        title: "Fulfillment Date",
        dataIndex: "fulfillmentDate",
        width: 120,
    }, {
        title: "SO#",
        dataIndex: "saleOrder.soNo",
    }, {
        title: "Customer",
        dataIndex: "fulfillment.customerName",
    }, {
        title: "Reference",
        render: (col, record) => <Reference value={record.saleOrder?.reference} />,
    }, {
        title: "SO Date",
        dataIndex: "saleOrder.soDate",
        width: 105
    }, {
        title: "Item Number",
        dataIndex: "itemNo",
    }, {
        title: "Item Name",
        dataIndex: "itemName",
    }, {
        title: "Item Description",
        dataIndex: "description",
    }, {
        title: "Fulfilled Qty",
        render: (col, record) => <Number value={record.qtyFulfilled} />,
    }, {
        title: "Fulfilled Amount",
        render: (col, record) => <Currency value={record.qtyFulfilled * record.rate} />,
    }, {
        title: "SO Price",
        render: (col, record) => <Currency value={record.soQty * record.rate} />,
    }
];

export const SOFulfillmentReport = (props) => {
    const [items, setItems] = useState([]);
    const [itemIds, setItemIds] = useState();
    const [customers, setCustomers] = useState([]);
    const [customerId, setCustomerId] = useState();
    const [month, setMonth] = useState(3);
    const [totalFulfilledQty, setTotalFulfilledQty] = useState(0);
    const [totalFulfilledAmount, setTotalFulfilledAmount] = useState(0);
    const [selectedColumnNames, setSelectedColumnNames] = useState(ColumnsOptions);
    const [columns, setColumns] = useState(AllColumns);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
    });

    const getData = async (page) => {
        let startDate = dayjs().subtract(month, "months").format("YYYY-MM-DD");
        let endDate = dayjs().format("YYYY-MM-DD");
        const values = await SOFulfillmentAPI.report({
            page: page ?? 1,
            customerId: customerId,
            itemIds: itemIds,
            startDate,
            endDate
        })
        setData(values.items.records);
        setPagination((pagination) => ({
            ...pagination,
            total: values.items.total,
        }));
        setTotalFulfilledQty(values.totalQuantity);
        setTotalFulfilledAmount(values.totalAmount);
    }

    const saveProfile = () => {
        UserProfile.saveProfile(UserProfileType.SO_FULFILLMENT_REPORT, {
            columns: selectedColumnNames
        });
    }

    useEffect(() => {
        const getCustomersAndItems = async () => {
            const customers = await CustomerAPI.actives();
            const sortedCustomers = _.sortBy(customers, "customerName");
            setCustomers(sortedCustomers);
            const items = await ItemAPI.actives();
            const sortedItems = _.sortBy(items, "itemName");
            setItems(sortedItems);

        }
        getCustomersAndItems();
    }, [])

    useEffect(() => {
        const loadColumns = async () => {
            const cols = AllColumns.filter(col => selectedColumnNames.includes(col.title));
            setColumns(cols);
        }

        loadColumns();
    }, [selectedColumnNames])

    useEffect(() => {
        const load = async () => {
            const profile = await UserProfile.getProfile(UserProfileType.SO_FULFILLMENT_REPORT);
            if (profile) {
                profile.columns && setSelectedColumnNames(profile.columns);
            }
        };
        load();
    }, [])

    return (
        <Card title="Fulfillment Report - SO" extra={<Space>
            <span>Date Range: Last</span>
            <InputNumber
                style={{ width: 45 }}
                hideControl
                min={1}
                value={month}
                onChange={(val) => {
                    setMonth(val);
                }}
            />
            <span>Month</span>
            <Select
                showSearch
                maxTagCount={1}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                options={customers.map((customer) => {
                    return {
                        label: customer.customerName,
                        value: customer.id
                    }
                })}
                placeholder="Select Customers"
                allowClear
                style={{ width: 200 }}
                value={customerId}
                onChange={(value) => {
                    setCustomerId(value);
                }}
            />
            <Select
                mode="multiple"
                maxTagCount={1}
                showSearch
                options={_.map(items, (item) => {
                    return {
                        label: item.itemName,
                        value: item.id
                    }
                })}
                placeholder="Select Items"
                allowClear
                style={{ width: 200 }}
                value={itemIds}
                filterOption={(inputValue, option) =>
                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                onChange={(value) => {
                    setItemIds(value);
                }}
            />
            <Button type="primary" onClick={() => getData()}>Search</Button>
            <Button type="primary" onClick={async () => {
                const res = await SOFulfillmentAPI.exportReport({
                    customerId: customerId,
                    itemIds: itemIds,
                    startDate: dayjs().subtract(month, "months").format("YYYY-MM-DD"),
                    endDate: dayjs().format("YYYY-MM-DD")
                });
                FileUtil.download(res.data, "SO_Fulfillment_Report.xlsx");
            }}>Export</Button>
            <Button type="primary" onClick={async () => {
                await saveProfile();
                Message.success("Profile Saved");
            }} >Save Profile</Button>
        </Space>}>
            <Row justify="end">
                <Col flex="auto">
                    <Space>
                        <span>Fulfilled Qty: <Number value={totalFulfilledQty} /></span>
                        <span>Fulfilled Amount: <Currency value={totalFulfilledAmount} /></span>
                    </Space>
                </Col>
                <Col flex="100px">
                    <Select
                        mode="multiple"
                        bordered={false}
                        placeholder="Columns"
                        triggerProps={{
                            autoAlignPopupWidth: false,
                            autoAlignPopupMinWidth: true,
                        }}
                        value={selectedColumnNames}
                        renderTag={(props, index) => {
                            return index === 0 ? <span style={{ margin: "0 5px" }}>Columns</span> : null;
                        }}
                        onChange={(selected) => {
                            setSelectedColumnNames(selected);
                        }}
                        options={ColumnsOptions}
                    />
                </Col>
            </Row>
            <Table rowKey="id"
                columns={columns}
                scroll={{ x: 1000, y: 'calc(100vh - 360px)' }}
                data={data}
                noDataElement={<Empty description="Select filters and Click Search to Load Data" />}
                pagination={pagination}
                onChange={(pagination) => {
                    setPagination(pagination);
                    getData(pagination.current);
                }} />

        </Card>
    )
}
